import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Calendar } from 'primereact/calendar';
import * as XLSX from 'xlsx';

import PageHeader from '../components/ui/PageHeader';
import DialogHeader from '../components/ui/DialogHeader';
import ActionToolbar from '../components/ui/ActionToolbar';
import { ExpedienteEntity } from '../entity/ExpedienteEntity';
import './Usuario.css';

export const PADExpedientePage = () => {

    const emptyExpediente = ExpedienteEntity

    const { axiosInstance } = useAuth();
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [expedienteDialog, setExpedienteDialog] = useState(false);
    const [expediente, setExpediente] = useState(emptyExpediente);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [soloConcluidos, setSoloConcluidos] = useState(false);
    const [optionsPersona, setOptionsPersona] = useState([]);

    const [importing, setImporting] = useState(false);
    const [errorReport, setErrorReport] = useState([]);
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        sortField: 'id',
        sortOrder: -1
    });

    const toast = useRef(null);
    const dt = useRef(null);

    const fetchPersonas = useCallback(async () => {
        try {
            const response = await axiosInstance.get("persona/", {
                params: { page: 0, size: 5000 }
            });
            if (response.data?.content) {
                const mapped = response.data.content.map(p => ({
                    label: `${p.docIdentidad} - ${p.nombre} ${p.apellido}`.toUpperCase(),
                    value: p.id
                }));
                mapped.sort((a, b) => a.label.localeCompare(b.label));
                setOptionsPersona(mapped);
            }
        } catch (error) {
            console.error("Error", error);
        }
    }, [axiosInstance]);

    const fetchExpedientes = useCallback(async () => {
        setLoading(true);
        try {
            const page = lazyParams.page;
            const size = lazyParams.rows;
            const sortField = lazyParams.sortField || 'id';
            const sortOrder = lazyParams.sortOrder === 1 ? 'asc' : 'desc';

            let url = soloConcluidos ? "pad-expediente/buscar-concluidos" : "pad-expediente";
            if (globalFilter.trim() && !soloConcluidos) {
                url = "pad-expediente/buscar";
            }

            const response = await axiosInstance.get(url, {
                params: {
                    page,
                    size,
                    filtro: globalFilter || "",
                    sort: `${sortField},${sortOrder}`
                }
            });
            setData(response.data.content);
            setTotalRecords(response.data.totalElements);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los datos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [lazyParams, globalFilter, soloConcluidos, axiosInstance]);

    useEffect(() => {
        fetchPersonas();
        fetchExpedientes();
    }, [fetchExpedientes, fetchPersonas]);

    const saveExpediente = async () => {
        setSubmitted(true);
        if (expediente.expediente.trim() && expediente.persona?.id) {
            try {
                if (expediente.id) {
                    await axiosInstance.put(`pad-expediente/${expediente.id}`, expediente);
                    toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Expediente Actualizado', life: 3000 });
                } else {
                    await axiosInstance.post("pad-expediente", expediente);
                    toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Expediente Creado', life: 3000 });
                }
                setExpedienteDialog(false);
                setExpediente(emptyExpediente);
                fetchExpedientes();
            } catch (error) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al procesar la solicitud', life: 3000 });
            }
        }
    };

    const confirmDelete = (rowData) => {
        confirmDialog({
            message: `¿Eliminar el expediente ${rowData.expediente}?`,
            header: 'Confirmar',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, eliminar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                await axiosInstance.delete(`pad-expediente/${rowData.id}`);
                fetchExpedientes();
                toast.current.show({ severity: 'info', summary: 'Eliminado', detail: 'Registro borrado', life: 3000 });
            }
        });
    };

    const onPage = (event) => {
        setLazyParams(prev => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: event.page
        }));
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _exp = { ...expediente };
        _exp[`${name}`] = val;
        setExpediente(_exp);
    };

    const personaBody = (rowData) => (
        <span>{`${rowData.persona.nombre} ${rowData.persona.apellido}`} <br />
            <small className="text-muted">{rowData.persona.docIdentidad}</small></span>
    );

    const actionBody = (rowData) => (
        <div className="flex gap-2">
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={() => { setExpediente(rowData); setExpedienteDialog(true); }} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger" onClick={() => confirmDelete(rowData)} />
        </div>
    );

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImporting(true);
        const reader = new FileReader();

        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];

                const jsonData = XLSX.utils.sheet_to_json(ws, { range: 1 });

                const errors = [];
                let successCount = 0;
                let duplicateCount = 0;

                for (let i = 0; i < jsonData.length; i++) {
                    const fila = jsonData[i];
                    const rowNum = i + 3;

                    const nroExpediente = String(fila['EXPEDIENTE'] || "").trim();
                    const nombreCompletoExcel = String(fila['PERSONA'] || "").trim().toUpperCase();

                    if (!nroExpediente && !nombreCompletoExcel) continue;

                    const yaExiste = data.some(exp => exp.expediente === nroExpediente);
                    if (yaExiste) {
                        duplicateCount++;
                        continue;
                    }

                    const personaEncontrada = optionsPersona.find(p => {
                        const partes = p.label.split(' - ');
                        if (partes.length < 2) return false;

                        const nombreEnSistema = partes[1].trim().toUpperCase();

                        return nombreEnSistema.includes(nombreCompletoExcel) ||
                            nombreCompletoExcel.includes(nombreEnSistema);
                    });

                    if (!personaEncontrada) {
                        errors.push({
                            fila: rowNum,
                            expediente: nroExpediente,
                            motivo: `No se encontró coincidencia para: "${nombreCompletoExcel}"`
                        });
                        continue;
                    }

                    const nuevoExpediente = {
                        expediente: nroExpediente,
                        tipoFalta: fila['TIPO DE FALTA'] || "",
                        sancion: fila['TIPO DE SANCION PROPUESTA/IMPUESTA'] || "",
                        estado: fila['ESTADO'] || "INGRESADO",
                        fecRegistro: new Date().toISOString().split('T')[0],
                        persona: { id: personaEncontrada.value } // Asignamos el ID
                    };

                    try {
                        await axiosInstance.post("pad-expediente", nuevoExpediente);
                        successCount++;
                    } catch (err) {
                        errors.push({
                            fila: rowNum,
                            expediente: nroExpediente,
                            motivo: err.response?.data?.message || 'Error de conexión'
                        });
                    }
                }

                setImporting(false);
                fetchExpedientes();

                let resumen = `Éxito: ${successCount}. `;
                if (duplicateCount > 0) resumen += `Duplicados omitidos: ${duplicateCount}.`;

                toast.current.show({
                    severity: errors.length > 0 ? 'warn' : 'success',
                    summary: 'Carga Finalizada',
                    detail: resumen,
                    life: 5000
                });

                if (errors.length > 0) {
                    setErrorReport(errors);
                    setShowErrorDialog(true);
                }

            } catch (error) {
                setImporting(false);
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Archivo incompatible', life: 3000 });
            }
        };

        reader.readAsBinaryString(file);
        e.target.value = null;
    };

    const importButton = (
        <div className="flex gap-2 mr-2">
            <input
                type="file"
                id="excel_upload"
                accept=".xlsx, .xls"
                hidden
                onChange={handleFileUpload}
            />
            <Button
                type="button"
                icon="pi pi-file-excel"
                label={importing ? "Cargando..." : "Importar Excel"}
                className="p-button-help"
                onClick={() => document.getElementById('excel_upload').click()}
                disabled={importing}
                style={{ backgroundColor: 'white', borderColor: '#cccccc' }}
            />
        </div>
    );

    return (
        <div className="usuario-page">
            <div className="container">
                <PageHeader
                    title="Expedientes PAD"
                    description="PROCEDIMIENTOS ADMINISTRATIVOS DISCIPLINARIOS"
                    icon="pi pi-folder-open"
                />
                <div className="main-content">
                    <div className="content-card">
                        <ConfirmDialog />
                        <Toast ref={toast} />
                        <ActionToolbar
                            onNew={() => { setExpediente(emptyExpediente); setExpedienteDialog(true); }}
                            newLabel="Nuevo Expediente"
                            searchValue={globalFilter}
                            onSearch={(val) => setGlobalFilter(val)}
                            extraActions={
                                <div className="flex align-items-center ml-2">
                                    {importButton}
                                    <label className="mr-2 font-bold">Sólo Concluidos</label>
                                    <Button
                                        icon={soloConcluidos ? "pi pi-check-square" : "pi pi-stop"}
                                        className={soloConcluidos ? "p-button-success" : "p-button-secondary"}
                                        onClick={() => setSoloConcluidos(!soloConcluidos)}
                                        style={{ backgroundColor: 'white', borderColor: '#cccccc' }}
                                    />
                                </div>
                            }
                        />
                        <DataTable ref={dt} value={data} lazy paginator first={lazyParams.first} rows={10}
                            totalRecords={totalRecords} onPage={onPage} loading={loading} emptyMessage="No existen registros."
                            dataKey="id" responsiveLayout="scroll" className="shadow-2">
                            <Column field="expediente" header="Nro. Expediente" />
                            <Column header="Persona" body={personaBody} />
                            <Column field="sancion" header="Sanción" />
                            <Column field="estado" header="Estado" />
                            <Column field="fecRegistro" header="Fecha Registro" body={(r) => new Date(r.fecRegistro).toLocaleDateString()} />
                            <Column field="fecPrescripcion" header="Fecha de Prescripción" body={(r) => r.fecPrescripcion ? new Date(r.fecPrescripcion).toLocaleDateString() : '-' } />
                            <Column header="Acciones" body={actionBody} />
                        </DataTable>

                        <Dialog
                            visible={expedienteDialog}
                            style={{ width: '700px', maxWidth: '95vw' }}
                            header={
                                <DialogHeader
                                    title={<span style={{ color: 'white' }}>{expediente.id ? 'Editar Expediente' : 'Nuevo Expediente'}</span>}
                                    subtitle={expediente.id ? 'Modificar datos del expediente' : 'Registrar un nuevo expediente'}
                                    icon="pi pi-plus"
                                />
                            }
                            modal
                            className="p-fluid custom-profile-dialog"
                            footer={
                                <div className="dialog-footer-buttons">
                                    <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={() => setExpedienteDialog(false)} />
                                    <Button label="Guardar" icon="pi pi-check" autoFocus onClick={saveExpediente} />
                                </div>
                            }
                            onHide={() => setExpedienteDialog(false)}>
                            <div className="p-fluid grid mt-2">
                                <div className="field col-6">
                                    <label htmlFor="expediente">Número de Expediente</label>
                                    <InputText id="expediente" value={expediente.expediente} onChange={(e) => onInputChange(e, 'expediente')} required className={submitted && !expediente.expediente ? 'p-invalid' : ''} />
                                </div>
                                <div className="field col-6">
                                    <label htmlFor="estado">Estado</label>
                                    <Dropdown value={expediente.estado} options={['INGRESADO', 'INVESTIGACION PRELIMINAR', 'FASE INSTRUCTORA', 'FASE RECURSAL', 'FASE SANCIONADORA', 'PENDIENTE', 'SE REMITIO A STPAD SUPLENTE', 'ARCHIVADO', 'CONCLUIDO']} onChange={(e) => onInputChange(e, 'estado')} placeholder="Seleccione Estado" />
                                </div>
                                <div className="field col-12">
                                    <label htmlFor="persona">1. Seleccionar a la Persona: <span className="p-error">*</span></label>
                                    <Dropdown
                                        id="persona"
                                        value={expediente.persona?.id}
                                        options={optionsPersona}
                                        onChange={(e) => setExpediente(prev => ({ ...prev, persona: { id: e.value } }))}
                                        optionLabel="label"
                                        optionValue="value"
                                        filter // Activa el buscador
                                        filterBy="label" // Filtra sobre el string que armamos (DNI + Nombre)
                                        filterMatchMode="contains" // Búsqueda flexible
                                        showClear
                                        appendTo="self"
                                        placeholder="Seleccione persona (Busque por DNI o Nombre)"
                                        virtualScrollerOptions={{
                                            itemSize: 38,
                                            rows: 5
                                        }}
                                        filterInputAutoFocus
                                    />
                                    {submitted && !expediente.persona?.id && <small className="p-error">La persona es obligatoria para el expediente.</small>}
                                </div>
                                <div className="field col-12">
                                    <label htmlFor="tipoFalta">Falta</label>
                                    <InputText id="tipoFalta" value={expediente.tipoFalta} onChange={(e) => onInputChange(e, 'tipoFalta')} className={submitted && !expediente.tipoFalta ? 'p-invalid' : ''} />
                                </div>
                                <div className="field col-12">
                                    <label htmlFor="observacion">Observaciones</label>
                                    <InputText id="observacion" value={expediente.observacion} onChange={(e) => onInputChange(e, 'observacion')} className={submitted && !expediente.observacion ? 'p-invalid' : ''} />
                                </div>
                                <div className="field col-6">
                                    <label>Fecha de Registro</label>
                                    <Calendar value={expediente.fecRegistro ? new Date(expediente.fecRegistro) : null} onChange={(e) => onInputChange({ target: { value: e.value } }, 'fecRegistro')} />
                                </div>
                                <div className="field col-6">
                                    <label>Fecha de Prescripción</label>
                                    <Calendar value={expediente.fecPrescripcion ? new Date(expediente.fecPrescripcion) : null} onChange={(e) => onInputChange({ target: { value: e.value } }, 'fecPrescripcion')} />
                                </div>
                                <div className="field col-6">
                                    <label htmlFor="sancion">Sanción</label>
                                    <Dropdown value={expediente.sancion} options={['AMONESTACIÓN VERBAL', 'AMONESTACIÓN ESCRITA', 'SUSPENSIÓN', 'DESTITUCIÓN', 'INEXISTENCIA DE RESPONSABILIDAD', 'NO A LUGAR A TRAMITE PAD', 'SE DERIVA A ODANC', 'SE DERIVA A OTRAS STPAD', 'PRESCRITO']} onChange={(e) => onInputChange(e, 'sancion')} placeholder="Seleccione Sanción" />
                                </div>
                            </div>
                        </Dialog>

                        <Dialog
                            header="Reporte de Errores de Importación"
                            visible={showErrorDialog}
                            style={{ width: '50vw' }}
                            onHide={() => setShowErrorDialog(false)}
                        >
                            <DataTable value={errorReport} rows={5} paginator className="p-datatable-sm">
                                <Column field="fila" header="Fila" style={{ width: '10%' }} />
                                <Column field="expediente" header="Expediente" style={{ width: '30%' }} />
                                <Column field="motivo" header="Motivo del Error" className="text-danger" />
                            </DataTable>
                            <div className="flex justify-content-end mt-3">
                                <Button label="Cerrar" icon="pi pi-times" onClick={() => setShowErrorDialog(false)} />
                            </div>
                        </Dialog>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PADExpedientePage;